/**
 * Accounts + balances + statements API — wraps /api/finance/accounts.
 *
 * Shape mirrors the backend surface exactly; no client-side translation.
 */

import { apiClient, buildQueryString } from "@/shared/lib/api/client";
import type {
  AccountKind,
  Account,
  AccountCreateInput,
  AccountUpdateInput,
  BalanceSnapshot,
  Issuer,
  Statement,
} from "../types";

const BASE = "/api/finance";

export function listAccounts(params?: {
  account_kind?: AccountKind;
}): Promise<Account[]> {
  const qs = buildQueryString({
    account_kind: params?.account_kind,
  });
  return apiClient<Account[]>(`${BASE}/accounts${qs}`);
}

export function getAccount(accountId: string): Promise<Account> {
  return apiClient<Account>(`${BASE}/accounts/${accountId}`);
}

export function createAccount(data: AccountCreateInput): Promise<Account> {
  return apiClient<Account>(`${BASE}/accounts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateAccount(
  accountId: string,
  data: AccountUpdateInput,
): Promise<Account> {
  return apiClient<Account>(`${BASE}/accounts/${accountId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function listIssuers(): Promise<Issuer[]> {
  return apiClient<Issuer[]>(`${BASE}/issuers`);
}

// ===================
// Balances — append-only observations
// ===================

export function listBalances(
  accountId: string,
  params?: { since?: string; until?: string },
): Promise<BalanceSnapshot[]> {
  const qs = buildQueryString({
    since: params?.since,
    until: params?.until,
  });
  return apiClient<BalanceSnapshot[]>(
    `${BASE}/accounts/${accountId}/balances${qs}`,
  );
}

// ===================
// Statements
// ===================

/**
 * Every statement period for an account, newest first. The route takes no
 * pagination params (router.py:495-499) — the repository caps at 60 periods,
 * which is five years of monthly statements.
 */
export function listStatements(accountId: string): Promise<Statement[]> {
  return apiClient<Statement[]>(`${BASE}/accounts/${accountId}/statements`);
}
